import { TableFrame, Th, Td, NumTd } from './TableFrame';
import { eur, formatMetric, num, pct } from './format';
import type { DeliveryReport, ReportLine } from './types';

interface LineKpisTableProps {
  report: DeliveryReport;
}

/** Daily cap of a line, in € or in impressions, whichever DV360 has. */
export function lineCapText(line: ReportLine): string {
  if (line.cap_now !== null) return `${eur(line.cap_now, 0)}/día`;
  if (line.cap_imp !== null) return `${num(line.cap_imp)} impr./día`;
  return 'sin tope';
}

export function PacingTag({ type, period }: { type: string | null; period: string | null }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className="adv-tag adv-tag--info">{type ?? '–'}</span>
      {period && <span className="text-adv-body-s text-adv-tertiary">{period}</span>}
    </span>
  );
}

/** Accumulated KPIs per line, one column per metric of the plan. */
export function LineKpisTable({ report }: LineKpisTableProps) {
  const { lines, plan, camp, metrics_catalog: catalog } = report;
  const navIds = new Set(plan.nav.line_ids);

  return (
    <TableFrame
      head={
        <>
          <Th>Línea</Th>
          <Th>Pacing</Th>
          <Th numeric>Tope diario</Th>
          <Th numeric>Techo CPM</Th>
          {plan.metrics.map((metric) => (
            <Th key={metric.id} numeric>
              {metric.label}
              {plan.primary === metric.id ? ' · primario' : ''}
            </Th>
          ))}
          <Th numeric>% gasto</Th>
        </>
      }
    >
      {lines.map((line) => (
        <tr key={line.id} className="hover:bg-adv-sunken">
          <Td className="min-w-[180px]">
            <span className="font-bold">{line.name}</span>
            {navIds.has(line.id) && <span className="adv-tag adv-tag--gold ml-2">Nav. Activa</span>}
            <p className="text-adv-body-s text-adv-tertiary">
              {line.goal}
              {line.status !== 'Activa' && ` · ${line.status.toLowerCase()}`}
            </p>
          </Td>
          <Td>
            <PacingTag type={line.pacing.type} period={line.pacing.period} />
          </Td>
          <NumTd>{lineCapText(line)}</NumTd>
          <NumTd>{eur(line.ceiling)}</NumTd>
          {plan.metrics.map((metric) => (
            <NumTd key={metric.id}>{formatMetric(catalog, metric.id, line.m[metric.id])}</NumTd>
          ))}
          <NumTd>{pct(line.share, 0)}</NumTd>
        </tr>
      ))}
      <tr className="bg-adv-sunken font-bold">
        <Td>Total</Td>
        <Td />
        <Td />
        <Td />
        {plan.metrics.map((metric) => (
          <NumTd key={metric.id}>{formatMetric(catalog, metric.id, camp.m[metric.id])}</NumTd>
        ))}
        <NumTd>100 %</NumTd>
      </tr>
    </TableFrame>
  );
}
